import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { MdDeleteForever } from "react-icons/md";
import DebounceInput from "react-debounce-input";
import Modal from "./modal";
import { getDaysInMonth, getDateKey } from "../utils";
import { HOLIDAY_COUNTRIES, getContractHolidayCountry, getHolidayName } from "../utils/holidays";

const ActivityTable = ({ user, date }) => {
  const [activities, setActivities] = useState([]);
  const [todayKey, setTodayKey] = useState(() => getDateKey(new Date()));
  const [showForecast, setShowForecast] = useState(() => {
    try {
      return localStorage.getItem("activity_show_forecast") !== "false";
    } catch (e) {
      return true;
    }
  });
  const [holidayCountry, setHolidayCountry] = useState(() => getContractHolidayCountry(user));
  const navigate = useNavigate();

  useEffect(() => {
    getActivities();
  }, [date, user]);

  // The holiday calendar follows the contract by default; a manual choice is remembered per person.
  useEffect(() => {
    let saved = null;
    try {
      saved = localStorage.getItem(`activity_holidays_${user._id}`);
    } catch (e) {}
    setHolidayCountry(saved || getContractHolidayCountry(user));
  }, [user]);

  function onChangeHolidayCountry(value) {
    setHolidayCountry(value);
    try {
      localStorage.setItem(`activity_holidays_${user._id}`, value);
    } catch (e) {}
  }

  useEffect(() => {
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const timeout = window.setTimeout(() => setTodayKey(getDateKey(new Date())), tomorrow.getTime() - now.getTime() + 1000);
    return () => window.clearTimeout(timeout);
  }, [todayKey]);

  const days = getDaysInMonth(date.getMonth(), date.getFullYear());
  // A day off is a weekend or a public holiday: nothing is expected on it.
  const holidayNames = days.map((day) => getHolidayName(day, holidayCountry));
  const offDays = days.map((day, i) => day.getDay() === 0 || day.getDay() === 6 || !!holidayNames[i]);
  const dailyTotals = days.map((_, i) => activities.reduce((acc, a) => acc + (a.detail[i]?.value || 0), 0));

  // Weeks (Mon → Sun) clipped to the month, with the hours expected (8h per working day) vs logged.
  const weeks = [];
  days.forEach((day, i) => {
    if (i === 0 || day.getDay() === 1) weeks.push({ start: i, length: 0, expected: 0, logged: 0, lastKey: "" });
    const week = weeks[weeks.length - 1];
    week.length += 1;
    week.lastKey = getDateKey(day);
    week.logged += dailyTotals[i];
    if (!offDays[i]) week.expected += 8;
  });

  function toggleForecast() {
    const next = !showForecast;
    setShowForecast(next);
    try {
      localStorage.setItem("activity_show_forecast", String(next));
    } catch (e) {}
  }

  // Actuals and forecast are both one entry per day of the month; an activity saved without one of them gets empty cells so the row is editable.
  const emptyDays = () => days.map((day) => ({ date: day, value: 0 }));
  const ensureDaily = (activity) => ({
    ...activity,
    detail: activity.detail?.length ? activity.detail : emptyDays(),
    predicted_detail: activity.predicted_detail?.length ? activity.predicted_detail : emptyDays(),
  });

  async function getActivities() {
    try {
      const { data } = await api.post(`/activity/search`, { date: date.getTime(), userId: user._id });
      setActivities(data.map(ensureDaily));
    } catch (e) {
      console.error(e);
    }
  }

  const onAddBudget = async (budget) => {
    const found = activities.find((a) => budget._id === a.budgetId);
    if (found) return toast.error(`Budget ${budget.name} already added !`);
    const { data, code } = await api.post(`/activity`, { budgetId: budget._id, userId: user._id, date: date.getTime(), detail: emptyDays(), predicted_detail: emptyDays() });
    if (code) return toast.error(code);
    toast.success(`Budget ${budget.name} added`);
    setActivities([...activities, ensureDaily(data)]);
  };

  async function onSave() {
    for (let i = 0; i < activities.length; i++) {
      if (!activities[i].total && !activities[i].predicted_total) continue;

      const { code } = await api.post(`/activity`, activities[i]);
      if (code) {
        toast.error(`${activities[i].projectName} not saved : ${code}`);
      } else {
        toast.success(`Saved ${activities[i].projectName}`);
      }
    }
  }

  async function onDelete(i) {
    if (!window.confirm("Are you sure ?")) return;
    await api.remove(`/activity/${activities[i]._id}`);
    const newActivities = [...activities];
    newActivities.splice(i, 1);
    setActivities(newActivities);
    toast.success(`Deleted`);
  }

  function onUpdateValue(i, j, value) {
    if (!user.tjm) return toast.error("Please contact an admin to set your TJM");
    if (!user.tjms) return toast.error("Please contact an admin to set your TJMS");

    const n = [...activities];

    n[i].detail[j].value = value;
    n[i].total = n[i].detail.reduce((acc, b) => acc + b.value, 0);

    if (n[i].project === "Unavailable") {
      n[i].cost = 0;
      n[i].value = 0;
    } else {
      n[i].cost = (n[i].total / 8) * user.tjm;
      n[i].value = (n[i].total / 8) * (n[i].userTjms || 0);
    }

    setActivities(n);
  }

  function onUpdatePredictedValue(i, j, value) {
    const n = [...activities];
    n[i].predicted_detail[j].value = value;
    n[i].predicted_total = n[i].predicted_detail.reduce((acc, b) => acc + (b.value || 0), 0);
    setActivities(n);
  }

  const getWorkingDays = () => offDays.filter((off) => !off).length;

  const getTotal = () => activities.reduce((acc, a) => acc + a.total, 0) / 8;

  const getTotalPredicted = () => activities.reduce((acc, a) => acc + (a.predicted_total || 0), 0) / 8;

  const getProjectActualDays = (projectName) => {
    const projectActivities = activities.filter((activity) => activity.projectName === projectName);
    const totalHours = projectActivities.reduce((acc, activity) => acc + activity.total, 0);
    return (totalHours / 8).toFixed(1);
  };

  const getGroupedActivities = () => {
    const grouped = {};
    activities.forEach((activity) => {
      if (!grouped[activity.projectName]) {
        grouped[activity.projectName] = [];
      }
      grouped[activity.projectName].push(activity);
    });
    return grouped;
  };

  return (
    <div className="w-screen md:w-full">
      <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2 border-b border-gray-100 px-6 py-3">
        <div className="flex flex-wrap items-center gap-5">
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-[#2d6a4f] inline-block" />
            <span className="text-xs text-gray-500">Heures travaillées</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-[#52b788] inline-block" />
            <span className="text-xs text-gray-500">Partiel</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded bg-[#b7e4c7] inline-block" />
            <span className="text-xs text-gray-500">Congés / Indispo</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded border border-dashed border-slate-400 inline-block" />
            <span className="text-xs text-gray-500">Forecast</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-emerald-700">✓</span>
            <span className="text-xs text-gray-500">Semaine complète (8h × jours ouvrés)</span>
          </div>
        </div>
        <div className="flex items-center gap-5">
          <label className="flex items-center gap-2 text-xs text-gray-500">
            Jours fériés
            <select
              className="rounded-md border-gray-200 py-1 pl-2 pr-7 text-xs text-gray-700 focus:border-gray-400 focus:ring-0"
              value={holidayCountry}
              onChange={(e) => onChangeHolidayCountry(e.target.value)}>
              {HOLIDAY_COUNTRIES.map((country) => (
                <option key={country.value} value={country.value}>
                  {country.label}
                  {country.value === getContractHolidayCountry(user) ? " (contrat)" : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="flex cursor-pointer select-none items-center gap-2 text-xs text-gray-500">
            <input type="checkbox" className="rounded border-gray-300 text-gray-700 focus:ring-0" checked={showForecast} onChange={toggleForecast} />
            Show forecast
          </label>
        </div>
      </div>
      <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "calc(100vh - 300px)" }}>
        <table className="w-full" style={{ borderSpacing: 0, tableLayout: "auto" }}>
          <thead className="sticky top-0 z-10 bg-white">
            <tr>
              <th />
              <th />
              <th />
              {weeks.map((week) => (
                <WeekStatus key={week.start} week={week} todayKey={todayKey} />
              ))}
              <th />
            </tr>
            <tr className="border-b border-gray-200">
              <th className="py-2 text-[11px] font-semibold text-gray-400 text-left pl-4 uppercase tracking-wider w-[70px]"></th>
              <th className="py-2 text-[11px] font-semibold text-gray-400 text-center w-[60px] uppercase tracking-wider">Forecast</th>
              <th className="py-2 text-[11px] font-semibold text-gray-400 text-center w-[60px] uppercase tracking-wider">Actual</th>
              {days.map((e, i) => {
                const dayNames = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
                const _date = new Date(e);
                const day = _date.getDay();
                const weekday = dayNames[day];
                const dateNum = _date.getDate();
                const isWeekend = day == 0 || day == 6;
                const isToday = getDateKey(_date) === todayKey;
                const holidayName = holidayNames[i];
                const dayTextColor = holidayName ? "text-amber-600" : isWeekend ? "text-gray-300" : "text-gray-400";
                return (
                  <th className={`text-center py-1.5 ${offDays[i] ? "bg-gray-50" : ""}`} style={{ width: 36, minWidth: 36, maxWidth: 36 }} key={e} day={day}>
                    <div className={`text-[10px] font-medium ${dayTextColor} uppercase leading-tight`}>{weekday}</div>
                    <div
                      className="group relative mx-auto flex h-[1.625rem] w-7 cursor-help items-center justify-center"
                      title={holidayName || undefined}
                      aria-label={holidayName || undefined}>
                      <time
                        dateTime={getDateKey(_date)}
                        aria-current={isToday ? "date" : undefined}
                        className={`flex h-[1.625rem] w-[1.625rem] items-center justify-center rounded-full text-[0.8125rem] font-bold leading-none ${
                          isToday ? "bg-gray-900 text-white" : holidayName ? "text-amber-600" : isWeekend ? "text-gray-300" : "text-gray-700"
                        }`}>
                        {dateNum}
                      </time>
                      {holidayName && (
                        <span className="pointer-events-none absolute left-1/2 top-full z-[9999] mt-1 hidden -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-[10px] font-semibold normal-case text-white shadow-lg group-hover:block">
                          {holidayName}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
              <th style={{ width: 32, minWidth: 32 }} />
            </tr>
          </thead>
          <tbody>
            {Object.entries(getGroupedActivities()).map(([projectName, projectActivities]) => (
              <React.Fragment key={projectName}>
                {projectActivities.map((activity, budgetIndex) => {
                  const globalIndex = activities.findIndex((item) => item._id === activity._id);
                  const isFirstBudget = budgetIndex === 0;
                  const rowSpan = isFirstBudget ? projectActivities.length * (showForecast ? 2 : 1) : 0; // one actual row (+ one forecast row) per budget
                  const isLeave =
                    activity.projectName === "Leave paid" || activity.projectName === "Unavailable" || activity.projectName === "RTT" || activity.projectName === "Arret Maladie";

                  return (
                    <React.Fragment key={`budget-${activity._id}`}>
                      <tr className={`hover:bg-[#faf8f5]/60 transition-colors ${showForecast ? "" : "border-b border-gray-100"}`}>
                        <th className="w-[70px] text-left">
                          <div className="flex flex-1 items-center justify-between gap-1 px-4 py-2">
                            <div className="flex flex-col flex-1 items-start">
                              {isFirstBudget ? (
                                <div
                                  className="text-[13px] font-bold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                                  onClick={() => navigate(`/project/${activity.projectId}`)}>
                                  {activity.projectName}
                                </div>
                              ) : (
                                <div className="text-[11px] text-gray-500">{activity.projectName}</div>
                              )}
                              <div className="text-[11px] text-gray-400 font-normal">{activity.budgetName}</div>
                            </div>
                          </div>
                        </th>
                        <td className="py-1.5 text-center w-[60px]" rowSpan={showForecast ? 2 : 1}>
                          <div className="text-[13px] font-medium text-gray-400 tabular-nums">{((activity.predicted_total || 0) / 8).toFixed(1)}d</div>
                        </td>
                        {isFirstBudget ? (
                          <th className="py-1.5 text-center w-[60px]" rowSpan={rowSpan}>
                            <div className="text-[13px] font-bold text-gray-800 tabular-nums">{getProjectActualDays(projectName)}d</div>
                            <div className="text-[10px] text-gray-400 font-normal tabular-nums">
                              {(() => {
                                const actualDays = parseFloat(getProjectActualDays(projectName));
                                const totalDays = getTotal();
                                return totalDays > 0 ? `${((actualDays / totalDays) * 100).toFixed(1)}%` : "0%";
                              })()}
                            </div>
                          </th>
                        ) : null}
                        {activity.detail.map((detailDay, dayIndex) => {
                          return (
                            <Field
                              key={`${activity.projectName} ${dayIndex}`}
                              offDay={offDays[dayIndex]}
                              freezed={activity.freezed}
                              value={detailDay.value || 0}
                              isLeave={isLeave}
                              onChange={(event) => onUpdateValue(globalIndex, dayIndex, parseFloat(event.target.value || 0))}
                            />
                          );
                        })}
                        <td style={{ width: 32, minWidth: 32 }} className="py-1">
                          <div className="flex justify-center cursor-pointer text-gray-300 hover:text-red-500 transition-colors text-base">
                            <MdDeleteForever onClick={() => onDelete(globalIndex)} />
                          </div>
                        </td>
                      </tr>
                      {showForecast && (
                        <tr className="border-b border-gray-100">
                          <th className="w-[70px] text-left">
                            <div className="px-4 pb-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-400">Forecast</div>
                          </th>
                          {activity.predicted_detail.map((forecastDay, dayIndex) => (
                            <Field
                              key={`forecast-${activity._id}-${dayIndex}`}
                              variant="forecast"
                              offDay={offDays[dayIndex]}
                              freezed={activity.freezed}
                              value={forecastDay.value || 0}
                              onChange={(event) => onUpdatePredictedValue(globalIndex, dayIndex, parseFloat(event.target.value || 0))}
                            />
                          ))}
                          <td style={{ width: 32, minWidth: 32 }} />
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}
            <tr>
              <td colSpan={days.length + 4} className="h-6" />
            </tr>
            <tr className="border-t-2 border-gray-200">
              <th className="py-2.5 text-left pl-4">
                <span className="text-[13px] font-bold text-gray-800">Total</span>
              </th>
              <th className="py-2.5 text-center text-[13px] font-medium text-gray-400 tabular-nums">{`${getTotalPredicted().toFixed(1)}/${getWorkingDays()}d`}</th>
              <th className="py-2.5 text-center text-[13px] font-bold text-gray-800 tabular-nums">{`${getTotal()}/${getWorkingDays()}d`}</th>
              {days.map((e, i) => {
                // A past working day with less than 8h logged is flagged so the gap is easy to spot.
                const missing = !offDays[i] && getDateKey(e) < todayKey && dailyTotals[i] < 8;
                return <Field key={`day-${i}`} value={dailyTotals[i]} offDay={offDays[i]} missing={missing} disabled />;
              })}
              <th style={{ width: 32, minWidth: 32 }} />
            </tr>
            {showForecast && (
              <tr className="border-b-2 border-gray-200">
                <th className="pb-2.5 pt-1 text-left pl-4">
                  <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Forecast</span>
                </th>
                <th />
                <th />
                {days.map((e, i) => {
                  const v = activities.reduce((acc, a) => acc + (a.predicted_detail[i]?.value || 0), 0);
                  return <Field key={`forecast-day-${i}`} value={v} variant="forecast" offDay={offDays[i]} disabled />;
                })}
                <th style={{ width: 32, minWidth: 32 }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add a new budget entry */}
      <div className="border-t border-gray-100 px-4 py-3">
        <AddBudget onAdd={onAddBudget} user={user} />
      </div>

      <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-3 flex justify-end items-center gap-4 z-50">
        <button className="bg-gray-900 text-white text-sm font-semibold px-6 py-2.5 rounded-lg hover:bg-gray-800 transition-colors" onClick={onSave}>
          Save
        </button>
      </div>
    </div>
  );
};

const AddBudget = ({ onAdd, user }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [budgets, setBudgets] = useState([]);
  const [lastMonthBudgets, setLastMonthBudgets] = useState([]);

  useEffect(() => {
    onSearch(search);
  }, [search]);

  useEffect(() => {
    getLastMonthBudgets();
    return () => setLastMonthBudgets([]);
  }, [user]);

  async function getLastMonthBudgets() {
    const lastMonth = new Date();
    lastMonth.setUTCMonth(lastMonth.getUTCMonth() - 1);
    lastMonth.setUTCDate(1);
    lastMonth.setUTCHours(0, 0, 0, 0);

    const { data: activities, ok } = await api.post(`/activity/search`, {
      date: lastMonth.getTime(),
      userId: user._id,
    });
    if (!ok) return;

    const budgetIds = [...new Set(activities.map((item) => item.budgetId))];

    const { data: budgets } = await api.post(`/budget/search`, {
      ids: budgetIds,
      status: "active",
    });

    setLastMonthBudgets(budgets);
  }

  async function onSearch(str) {
    const { data } = await api.post(`/budget/search`, { search: str, limit: 10, status: "active" });
    setBudgets(data);
  }

  return (
    <div>
      <div>
        <button
          className="text-sm text-gray-500 hover:text-gray-800 font-medium transition-colors border border-gray-200 rounded-lg px-4 py-2 hover:border-gray-300"
          onClick={() => setOpen(true)}>
          + Add a new entry
        </button>
      </div>
      <Modal isOpen={open} onClose={() => setOpen(false)} className="w-10/12 mx-auto">
        <div className="border-b pb-4 p-6">
          <h2 className="text-xl font-bold">Add a new activity entry</h2>
          <p className="text-gray-600 text-sm">Select from common budgets or search for specific ones</p>
        </div>

        <div className="flex flex-col md:flex-row gap-6 p-6">
          <div className="md:w-1/2">
            <h3 className="text-lg font-semibold mb-3">Search Budgets</h3>
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <DebounceInput
                debounceTimeout={300}
                id="search"
                className="form-input block w-full pl-10 p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-blue-500 transition-all"
                placeholder="Search budgets..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="sticky top-0 max-h-[70vh] overflow-y-auto">
              <div className="space-y-3">
                {budgets.map((e) => (
                  <div
                    key={e._id}
                    className="group bg-white rounded-lg border hover:border-blue-500 shadow-sm hover:shadow-md p-4 cursor-pointer transition-all duration-200"
                    onClick={() => {
                      setOpen(false);
                      onAdd(e);
                    }}>
                    <div className="flex items-center gap-3">
                      <img src={e.projectLogo} className="w-12 h-12 rounded-lg object-cover" alt={e.projectName} />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold group-hover:text-blue-600">
                          {e.projectName} {e.year}
                        </div>
                        <div className="font-medium text-gray-700">{e.name}</div>
                        <div className="text-sm text-gray-500 line-clamp-2">{e.description}</div>
                      </div>
                    </div>
                  </div>
                ))}
                {search && budgets.length === 0 && (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <div className="text-gray-400 mb-2">No results found</div>
                    <p className="text-sm text-gray-500">Try adjusting your search terms</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="md:w-1/2">
            <h3 className="text-lg font-semibold mb-3">Last Month's Budgets</h3>
            <div className="grid grid-cols-2 gap-4 max-h-[75vh] overflow-y-auto">
              {lastMonthBudgets.map((e) => (
                <div
                  key={e._id}
                  className="group bg-white rounded-lg border hover:border-blue-500 shadow-sm hover:shadow-md p-4 cursor-pointer transition-all duration-200"
                  onClick={() => {
                    setOpen(false);
                    onAdd(e);
                  }}>
                  <div className="flex items-center gap-3">
                    <img src={e.projectLogo} className="w-12 h-12 rounded-lg object-contain" alt={e.projectName} />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold group-hover:text-blue-600">
                        {e.projectName} {e.year}
                      </div>
                      <div className="font-medium text-gray-700">{e.name}</div>
                      <div className="text-sm text-gray-500 line-clamp-2 hover:line-clamp-none">{e.description}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// Same thresholds for the palettes: worked hours (green), leave (pale green). Forecast is drawn as a dashed outline instead (see Field).
const FIELD_PALETTES = {
  actual: [
    { min: 7, bg: "#2d6a4f", text: "#fff" },
    { min: 5, bg: "#40916c", text: "#fff" },
    { min: 3, bg: "#52b788", text: "#fff" },
    { min: 0, bg: "#95d5b2", text: "#1b4332" },
  ],
  leave: [
    { min: 7, bg: "#b7e4c7", text: "#1b4332" },
    { min: 5, bg: "#b7e4c7", text: "#1b4332" },
    { min: 3, bg: "#d8f3dc", text: "#2d6a4f" },
    { min: 0, bg: "#d8f3dc", text: "#2d6a4f" },
  ],
};

const WeekStatus = ({ week, todayKey }) => {
  if (!week.expected) return <th colSpan={week.length} />;

  const complete = week.logged >= week.expected;
  const isPast = week.lastKey < todayKey;
  const progress = Math.min(100, (week.logged / week.expected) * 100);
  const color = complete ? "text-emerald-700" : isPast ? "text-amber-600" : "text-gray-400";
  const barColor = complete ? "bg-emerald-500" : isPast ? "bg-amber-400" : "bg-gray-300";

  return (
    <th colSpan={week.length} className="px-1 pt-2">
      <div className={`flex items-center justify-center gap-1 text-[10px] font-semibold tabular-nums ${color}`}>
        {complete && <span aria-hidden>✓</span>}
        {`${week.logged}/${week.expected}h`}
      </div>
      <div className="mt-1 h-1 overflow-hidden rounded-full bg-gray-100">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${progress}%` }} />
      </div>
    </th>
  );
};

const Field = ({ value = 0, onChange, freezed, isLeave, offDay, missing, variant = "actual", ...rest }) => {
  const hasValue = value > 0;
  const isForecast = variant === "forecast";
  const palette = FIELD_PALETTES[isLeave ? "leave" : "actual"];
  const shade = hasValue && !isForecast ? palette.find((step) => value >= step.min) : null;

  // Actuals are solid badges; forecasts are dashed outlines, smaller and gray, so the two rows never look alike.
  const editable = !rest.disabled && freezed !== "yes";
  let badgeStyle = { backgroundColor: "transparent", color: "#cbd5e1" };
  // Empty editable cells keep a visible box so they still read as inputs.
  if (!hasValue && editable) badgeStyle = { backgroundColor: "#fff", color: "#cbd5e1", border: `1px ${isForecast ? "dashed" : "solid"} ${offDay ? "#eef0f3" : "#e2e8f0"}` };
  if (shade) badgeStyle = { backgroundColor: shade.bg, color: shade.text };
  if (hasValue && isForecast) badgeStyle = { backgroundColor: "transparent", color: "#64748b", border: "1px dashed #94a3b8" };
  if (missing) badgeStyle = { ...badgeStyle, boxShadow: "inset 0 0 0 1.5px #fbbf24" };

  return (
    <td className={`py-[3px] px-[2px] text-center ${offDay ? "bg-gray-50" : ""}`} style={{ width: 36, minWidth: 36, maxWidth: 36 }}>
      <div
        className={`relative mx-auto flex items-center justify-center transition-shadow focus-within:ring-2 focus-within:ring-gray-400 ${editable ? "hover:ring-1 hover:ring-gray-300" : ""}`}
        style={{
          width: isForecast ? 26 : 30,
          height: isForecast ? 22 : 28,
          borderRadius: 6,
          ...badgeStyle,
        }}>
        <input
          type="number"
          min={0}
          step="0.1"
          disabled={freezed === "yes"}
          value={hasValue ? parseFloat(value) : ""}
          placeholder={editable ? "" : "·"}
          onChange={onChange}
          {...rest}
          className={`w-full h-full text-center tabular-nums bg-transparent border-0 outline-none p-0 m-0 placeholder:text-gray-300 ${isForecast ? "text-[11px] font-medium" : "text-[12px] font-bold"}`}
          style={{
            color: badgeStyle.color,
            WebkitAppearance: "none",
            MozAppearance: "textfield",
          }}
        />
      </div>
    </td>
  );
};

export default ActivityTable;
